import React from 'react';
// import GridViewIcon from '@mui/icons-material/GridView';
import WindowIcon from '@mui/icons-material/Window';
import ViewListIcon from '@mui/icons-material/ViewList';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FilterListIcon from '@mui/icons-material/FilterList';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import {
  BookingFilterState,
  BookingSortDirection,
  BookingSortField,
  BookingStatusType,
  BookingViewMode,
  ResourceType,
} from '../../data/types';

const BOOKING_STATUSES: BookingStatusType[] = [
  'pending',
  'approved',
  'in_progress',
  'completed',
  'canceled',
];

const RESOURCE_TYPES: ResourceType[] = [
  'kitchen',
  'documentation',
  'training',
  'funding',
  'expert',
  'farm',
];

const SORT_OPTIONS: { value: BookingSortField; label: string }[] = [
  { value: 'start_time', label: 'Date / Time' },
  { value: 'resource_name', label: 'Resource Name' },
  { value: 'resource_type', label: 'Resource Type' },
  { value: 'status', label: 'Booking Status' },
  { value: 'total_hours', label: 'Duration' },
  { value: 'plan_rate', label: 'Plan Rate' },
  { value: 'total_cost', label: 'Total Cost' },
  { value: 'created', label: 'Created Date' },
];

type BookingViewControlsProps = {
  viewMode: BookingViewMode;
  onViewModeChange: (mode: BookingViewMode) => void;
  sortField: BookingSortField;
  onSortFieldChange: (field: BookingSortField) => void;
  sortDirection: BookingSortDirection;
  onSortDirectionChange: (direction: BookingSortDirection) => void;
  filters: BookingFilterState;
  onFiltersChange: (filters: BookingFilterState) => void;
  showFilters: boolean;
  onToggleFilters: () => void;
  activeFilterCount: number;
  onResetFilters: () => void;
  resultCount: number;
};

const toggleArrayItem = <T,>(arr: T[], item: T): T[] =>
  arr.includes(item) ? arr.filter((i) => i !== item) : [...arr, item];

const BookingViewControls: React.FC<BookingViewControlsProps> = ({
  viewMode,
  onViewModeChange,
  sortField,
  onSortFieldChange,
  sortDirection,
  onSortDirectionChange,
  filters,
  onFiltersChange,
  showFilters,
  onToggleFilters,
  activeFilterCount,
  onResetFilters,
  resultCount,
}) => {
  const viewButtonClass = (mode: BookingViewMode) =>
    `p-2 rounded-md transition-colors ${
      viewMode === mode
        ? 'bg-primaryDark-300/80 text-white border-none'
        : ' text-[#8A8A8A]/80 border-none bg-[#E9E9E9]/80 hover:bg-[#D9D9D9]/80'
    }`;

  return (
    <div className="w-full px-6 my-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex flex-row items-center gap-1">
          {/* <span className="text-sm font-raleway text-[#8A8A8A] mr-1">View:</span> */}
          <button
            type="button"
            title="Grid view"
            className={viewButtonClass('grid')}
            onClick={() => onViewModeChange('grid')}
          >
            <WindowIcon fontSize="small" />
          </button>
          <button
            type="button"
            title="List view"
            className={viewButtonClass('list')}
            onClick={() => onViewModeChange('list')}
          >
            <ViewListIcon fontSize="small" />
          </button>
          <button
            type="button"
            title="Calendar view"
            className={viewButtonClass('calendar')}
            onClick={() => onViewModeChange('calendar')}
          >
            <CalendarMonthIcon fontSize="small" />
          </button>
        </div>

        <div className="flex flex-row items-center gap-2 flex-wrap">
          <div className="flex flex-row items-center gap-2">
            <SwapVertIcon className="text-[#8A8A8A]" fontSize="small" />
            <select
              value={sortField}
              onChange={(e) => onSortFieldChange(e.target.value as BookingSortField)}
              className="border border-[#E9E9E9] rounded-lg px-3 py-2 text-sm font-raleway min-w-36 text-[#8A8A8A] bg-white"
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <select
              value={sortDirection}
              onChange={(e) => onSortDirectionChange(e.target.value as BookingSortDirection)}
              className="border border-[#E9E9E9] rounded-lg px-3 py-2 text-sm font-raleway min-w-32 text-[#8A8A8A] bg-white"
            >
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>

          <button
            type="button"
            onClick={onToggleFilters}
            className={`flex flex-row items-center gap-1 px-4 py-2 rounded-lg text-sm font-raleway font-semibold transition-colors ${
              showFilters || activeFilterCount > 0
                ? 'bg-[#013847] text-white'
                : 'bg-white text-primaryDark-200/70 border border-[#E9E9E9] hover:bg-[#f5f5f5]'
            }`}
          >
            <FilterListIcon fontSize="small" />
            Filters
            {activeFilterCount > 0 && (
              <span className="ml-1 bg-white text-[#013847] rounded-full px-2 text-xs">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* <p className="text-sm font-raleway text-[#8A8A8A] mt-2">
        {resultCount} booking{resultCount !== 1 ? 's' : ''} found
      </p> */}

      {showFilters && (
        <div className="mt-4 p-4 border border-[#E9E9E9] rounded-xl bg-[#FAFAFA]">
          <div className="flex flex-row justify-between items-center mb-4">
            <h3 className="font-raleway font-semibold text-[#013847]">Filter Bookings</h3>
            <button
              type="button"
              onClick={onResetFilters}
              className="text-sm font-raleway text-[#5DB6CE] font-semibold border-[#E9E9E9] hover:bg-[#E9E9E9] hover:text-[#013847] rounded-lg px-3 py-2"
            >
              Reset All
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-raleway font-semibold text-[#8A8A8A] mb-2">
                Booking Status
              </label>
              <div className="flex flex-wrap gap-2">
                {BOOKING_STATUSES.map((status) => (
                  <button
                    key={status}
                    type="button"
                    onClick={() =>
                      onFiltersChange({
                        ...filters,
                        statuses: toggleArrayItem(filters.statuses, status),
                      })
                    }
                    className={`px-3 py-1 rounded-lg text-xs font-medium font-raleway capitalize ${
                      filters.statuses.includes(status)
                        ? 'bg-[#013847] text-white'
                        : 'bg-white border border-[#E9E9E9] text-[#013847]'
                    }`}
                  >
                    {status.replace(/_/g, ' ')}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-raleway font-semibold text-[#8A8A8A] mb-2">
                Resource Type
              </label>
              <div className="flex flex-wrap gap-2">
                {RESOURCE_TYPES.map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() =>
                      onFiltersChange({
                        ...filters,
                        resourceTypes: toggleArrayItem(filters.resourceTypes, type),
                      })
                    }
                    className={`px-3 py-1 rounded-lg text-xs font-medium font-raleway capitalize ${
                      filters.resourceTypes.includes(type)
                        ? 'bg-[#013847] text-white'
                        : 'bg-white border border-[#E9E9E9] text-[#013847]'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-raleway font-semibold text-[#8A8A8A] mb-1">
                Resource Name
              </label>
              <input
                type="text"
                value={filters.resourceName}
                onChange={(e) =>
                  onFiltersChange({ ...filters, resourceName: e.target.value })
                }
                placeholder="Search by name..."
                className="w-full border border-[#E9E9E9] rounded-lg px-3 py-2 text-sm font-raleway"
              />
            </div>

            <div>
              <label className="block text-xs font-raleway font-semibold text-[#8A8A8A] mb-1">
                Date From
              </label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) =>
                  onFiltersChange({ ...filters, dateFrom: e.target.value })
                }
                className="w-full border border-[#E9E9E9] rounded-lg px-3 py-2 text-sm font-raleway"
              />
            </div>

            <div>
              <label className="block text-xs font-raleway font-semibold text-[#8A8A8A] mb-1">
                Date To
              </label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) =>
                  onFiltersChange({ ...filters, dateTo: e.target.value })
                }
                className="w-full border border-[#E9E9E9] rounded-lg px-3 py-2 text-sm font-raleway"
              />
            </div>

            <div>
              <label className="block text-xs font-raleway font-semibold text-[#8A8A8A] mb-1">
                Plan Rate ($)
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="0"
                  placeholder="Min"
                  value={filters.planRateMin}
                  onChange={(e) =>
                    onFiltersChange({ ...filters, planRateMin: e.target.value })
                  }
                  className="w-1/2 border border-[#E9E9E9] rounded-lg px-3 py-2 text-sm font-raleway"
                />
                <input
                  type="number"
                  min="0"
                  placeholder="Max"
                  value={filters.planRateMax}
                  onChange={(e) =>
                    onFiltersChange({ ...filters, planRateMax: e.target.value })
                  }
                  className="w-1/2 border border-[#E9E9E9] rounded-lg px-3 py-2 text-sm font-raleway"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-raleway font-semibold text-[#8A8A8A] mb-1">
                Total Cost ($)
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="0"
                  placeholder="Min"
                  value={filters.totalCostMin}
                  onChange={(e) =>
                    onFiltersChange({ ...filters, totalCostMin: e.target.value })
                  }
                  className="w-1/2 border border-[#E9E9E9] rounded-lg px-3 py-2 text-sm font-raleway"
                />
                <input
                  type="number"
                  min="0"
                  placeholder="Max"
                  value={filters.totalCostMax}
                  onChange={(e) =>
                    onFiltersChange({ ...filters, totalCostMax: e.target.value })
                  }
                  className="w-1/2 border border-[#E9E9E9] rounded-lg px-3 py-2 text-sm font-raleway"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-raleway font-semibold text-[#8A8A8A] mb-1">
                Duration (hours)
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  placeholder="Min"
                  value={filters.durationMin}
                  onChange={(e) =>
                    onFiltersChange({ ...filters, durationMin: e.target.value })
                  }
                  className="w-1/2 border border-[#E9E9E9] rounded-lg px-3 py-2 text-sm font-raleway"
                />
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  placeholder="Max"
                  value={filters.durationMax}
                  onChange={(e) =>
                    onFiltersChange({ ...filters, durationMax: e.target.value })
                  }
                  className="w-1/2 border border-[#E9E9E9] rounded-lg px-3 py-2 text-sm font-raleway"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingViewControls;
